import { useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { useReveal } from '@/hooks/useReveal';
import TrialModal from '@/components/TrialModal';
import { DOMAIN_SUMMARY } from '@/data/mockData';
import s from './DomainSummary.module.scss';

// ── 안전/환경 도메인 요약 (D안 Phase 2-④) ──────────────────────────
//   [2026-09-09 사용자] '표/틀' 금지 → 회사 슬로건·철학 표현. 배경 = 가로 100% 좌(안전)blue/우(환경)green 솔리드 통(청량).
//   ★등장 애니메이션(사용자 선택 4+3): ④커튼 분할(배경이 중앙에서 좌우로 열림) + ③마스크 업(모든 요소가 마스크 아래에서 위로).
//     구현: useReveal ref=section → .is-inview. 배경=::before clip-path(중앙→바깥). 각 요소=.up(overflow:hidden)+내부 translateY.
//     스태거=요소별 transition-delay(커튼 후 순차 상승). 마스크업을 '모든 요소'에 적용(사용자 강조).
const ICONS: Record<string, JSX.Element> = {
  safety: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M24 5l15 6v10c0 9-6.4 16.8-15 21-8.6-4.2-15-12-15-21V11l15-6z" />
      <path d="M17 24l5 5 9-10" />
    </svg>
  ),
  environment: (
    <svg viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 16h19a5 5 0 1 0-5-5" />
      <path d="M6 24.5h29a6 6 0 1 0-6-6" />
      <path d="M6 33h17a4.5 4.5 0 1 1-4.5 4.5" />
    </svg>
  ),
};
const d0 = (sec: number): CSSProperties => ({ transitionDelay: `${sec}s` }); // 마스크업 스태거

export default function DomainSummary() {
  const reveal = useReveal<HTMLElement>(); // section에 부여 → .is-inview로 커튼+마스크업 동시 제어
  const [demoOpen, setDemoOpen] = useState(false);
  return (
    <section className={s.section} ref={reveal}>
      <div className={s.inner}>
        <header className={s.head}>
          <span className={s.up}><p className={s.eyebrow} style={d0(0.42)}>{DOMAIN_SUMMARY.eyebrow}</p></span>
          <span className={s.up}><h2 className={s.title} style={d0(0.5)}>{DOMAIN_SUMMARY.title}</h2></span>
        </header>

        <div className={s.grid}>
          {DOMAIN_SUMMARY.domains.map((d) => {
            // 커튼(≈.62s) 이후 요소 순차 상승. 두 열 동일 딜레이 → 좌우 대칭 상승.
            const b = 0.6; let i = 0; const D = () => d0(b + (i++) * 0.075);
            return (
              <article key={d.key} className={`${s[d.key as 'safety' | 'environment']} ${s.col}`}>
                <span className={s.no} style={d0(0.55)} aria-hidden="true">{d.no}</span>
                <span className={s.up}><span className={s.icon} style={D()}>{ICONS[d.key]}</span></span>
                <span className={s.up}><p className={s.kicker} style={D()}>{d.tag}</p></span>
                <h3 className={s.headline}>
                  {d.slogan.split('\n').map((line, k) => (
                    <span className={s.up} key={k}><span className={s.slLine} style={D()}>{line}</span></span>
                  ))}
                </h3>
                <span className={s.up}><p className={s.desc} style={D()}>{d.sub}</p></span>
                <span className={s.up}><p className={s.keywords} style={D()}>{d.points.map((p) => p.label).join('  ·  ')}</p></span>
                <span className={s.up}>
                  <div className={s.actions} style={D()}>
                    <Link to={d.to} className={s.cta}>
                      {d.cta}
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9"><path d="M7 17L17 7M17 7H9M17 7v8" /></svg>
                    </Link>
                    {d.demo && (
                      <button type="button" className={s.link2} onClick={() => setDemoOpen(true)}>
                        <span className={s.play}><svg width="15" height="15" viewBox="0 0 24 24"><circle cx="12" cy="12" r="12" fill="currentColor" /><path d="M9.5 8l7 4-7 4z" fill="#fff" /></svg></span>
                        SaaS 데모 체험
                      </button>
                    )}
                    {d.altCta && d.altTo && <Link to={d.altTo} className={s.link2}>{d.altCta}</Link>}
                  </div>
                </span>
              </article>
            );
          })}
        </div>
      </div>
      <TrialModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </section>
  );
}
